/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { FC, ReactElement, useEffect, useState } from "react";

import Helmet from "react-helmet";

import { THEME_NAME, useThemeContext } from "~context/ThemeContext";
import {
  isInGroupRbCrbRebrandingIndeedFont1,
  isInGroupRbRescomRebrandToIndeed1
} from "~lib/abTestHelper";
import "../css/onetrust.scss";

type Props = {
  title: string;
  children?: ReactElement;
  description?: string;
  canonical?: string;
  lang?: string;
  meta?: any[];
  keywords?: any[];
  links?: any[];
  fromPage?: string;
};

interface Script {
  type: string;
  src?: string;
  innerHTML?: string;
  charset?: string;
  "data-domain-script"?: string;
}

const SEO: FC<Props> = ({
  description = "",
  lang = "en",
  meta = [],
  keywords = [],
  title,
  links = [],
  canonical,
  children,
  fromPage
}) => {
  const IS_SSR: boolean = typeof window === "undefined";
  const [isOneTrustPage, setIsOneTrustPage] = useState<boolean>(false);
  const [hasSeenBanner, setHasSeenBanner] = useState<boolean>(false);
  const className: string = IS_SSR ? "no-js" : "js";
  const pageTitle = title;
  const [
    isGroupRbCrbRebrandingIndeedFont1,
    setIsInGroupRbCrbRebrandingIndeedFont1
  ] = useState(true);

  const theme = useThemeContext();
  const isIndeedTheme = theme.mode === THEME_NAME.INDEED;

  const BANNER_KEY = "oneTrustBannerClosed";

  useEffect(() => {
    // Check localStorage for banner preference
    if (!IS_SSR) {
      const storedValue = localStorage.getItem(BANNER_KEY);
      const hasUserClosedBanner = storedValue === "true";
      setHasSeenBanner(hasUserClosedBanner);

      window.addEventListener("OneTrustGroupsUpdated", handleBannerClosed);
    }

    if (
      window.location.pathname === "/" ||
      window.location.pathname.indexOf("/cookies") !== -1
    ) {
      setIsOneTrustPage(!hasSeenBanner);
    } else {
      handleRemoveBanner();
    }

    Promise.all([
      isInGroupRbCrbRebrandingIndeedFont1(),
      isInGroupRbRescomRebrandToIndeed1()
    ]).then(([rebrandingIndeedFont, rebrandingToIndeed]) => {
      if (rebrandingIndeedFont || rebrandingToIndeed) {
        setIsInGroupRbCrbRebrandingIndeedFont1(true);
      }
    });

    // Listen to tab events to enable outlines (accessibility improvement)
    document.body.addEventListener("keyup", e => {
      if (e.code === "Tab") {
        document.body.classList.remove("no-focus-outline");
      }
    });
    document.body.addEventListener("mousedown", () => {
      document.body.classList.add("no-focus-outline");
    });

    return () => {
      if (!IS_SSR) {
        window.removeEventListener("OneTrustGroupsUpdated", handleBannerClosed);
      }
      document.body.removeEventListener("mousedown", () => {
        document.body.classList.add("no-focus-outline");
      });
      document.body.removeEventListener("keyup", e => {
        if (e.code === "Tab") {
          document.body.classList.remove("no-focus-outline");
        }
      });
    };
  }, [hasSeenBanner]);

  const handleBannerClosed = () => {
    // When user interacts with the banner, this is saved in localStorage
    if (!IS_SSR) {
      localStorage.setItem(BANNER_KEY, "true");
      setHasSeenBanner(true);
      setIsOneTrustPage(false);
    }
  };

  const handleRemoveBanner = () => {
    const oneTrustBannerScript = document.querySelector(
      "script[src*='otBannerSdk']"
    );
    oneTrustBannerScript && oneTrustBannerScript.remove();

    const oneTrustBanner = document.getElementById("onetrust-consent-sdk");
    oneTrustBanner && oneTrustBanner.remove();
  };

  const getOneTrustScript = (): Array<Script> => {
    let oneTrustScriptArr = [];
    if (!isOneTrustPage) {
      return oneTrustScriptArr;
    }

    oneTrustScriptArr = [
      {
        type: "text/javascript",
        innerHTML: "<!-- OptanonConsentNoticeStart -->"
      },
      {
        type: "text/javascript",
        src:
          "https://cdn.cookielaw.org/consent/fe8596b2-afcf-4868-8c33-b151244324a5/OtAutoBlock.js"
      },
      {
        type: "text/javascript",
        src: "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js",
        charset: "UTF-8",
        "data-domain-script": "fe8596b2-afcf-4868-8c33-b151244324a5"
      },
      {
        type: "text/javascript",
        innerHTML: "function OptanonWrapper() { }"
      },
      {
        type: "text/javascript",
        innerHTML: "<!-- OptanonConsentNoticeEnd -->"
      }
    ];
    return oneTrustScriptArr;
  };

  const getSiteMetadata = () => {
    return {
      siteUrl: `https://www.resume.com`,
      title:
        fromPage == "homepage" ? `Resume Templates` : `Indeed Resume Builder`,
      description: `100% free resume builder to make, save and print a professional resume in minutes. Make applying faster and easier by connecting to millions of jobs today.`,
      author: `@resumecom`
    };
  };

  const data = getSiteMetadata();
  const metaDescription: string = description || data.description;

  return (
    <Helmet
      htmlAttributes={{
        lang,
        className
      }}
      bodyAttributes={{
        class: `no-focus-outline ${
          isGroupRbCrbRebrandingIndeedFont1 ? "indeed-font" : ""
        } ${isIndeedTheme ? "indeed-theme" : "rescom-theme"}`
      }}
      title={pageTitle}
      titleTemplate={`%s | ${data.title}`}
      link={links}
      script={[
        {
          type: "text/javascript",
          innerHTML: isOneTrustPage
            ? "!document.originalCreateElement && (document.originalCreateElement = document.createElement);"
            : "document.originalCreateElement && (document.createElement = document.originalCreateElement);"
        },
        ...getOneTrustScript(),
        {
          type: "text/javascript",
          innerHTML:
            "document.documentElement.className = document.documentElement.className.replace(/\\bno-js\\b/,'js');",
          async: ""
        }
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription
        },
        {
          property: `og:title`,
          content: title
        },
        {
          property: `og:description`,
          content: metaDescription
        },
        {
          property: `og:type`,
          content: `website`
        },
        {
          name: `twitter:card`,
          content: `summary`
        },
        {
          name: `twitter:creator`,
          content: data.author
        },
        {
          name: `twitter:title`,
          content: title
        },
        {
          name: `twitter:description`,
          content: metaDescription
        }
      ]
        .concat(
          keywords.length > 0
            ? {
                name: `keywords`,
                content: keywords.join(`, `)
              }
            : []
        )
        .concat(meta)}
    >
      {canonical && <link rel="canonical" href={`${canonical}`} />}
      {children}
    </Helmet>
  );
};

export default SEO;
